import { useState, useEffect } from "react";
import { getHoursCloseTicketsAuto } from "../../config";
import toastError from "../../errors/toastError";

import api from "../../services/api";

const ticketsCache = new Map();
const TICKETS_CACHE_MAX_AGE_MS = 5 * 60 * 1000;

const getCacheKey = params => JSON.stringify(params);

const getCachedTickets = key => {
    const cached = ticketsCache.get(key);

    if (!cached || Date.now() - cached.updatedAt > TICKETS_CACHE_MAX_AGE_MS) {
        ticketsCache.delete(key);
        return null;
    }

    return cached;
};

export const clearTicketsCache = () => {
    ticketsCache.clear();
};

const useTickets = ({
    searchParam,
    pageNumber,
    status,
    date,
    showAll,
    queueIds,
    withUnreadMessages,
    refreshKey = 0,
}) => {
    const cacheKey = getCacheKey({
        searchParam,
        pageNumber,
        status,
        date,
        showAll: showAll === true || showAll === "true",
        queueIds,
        withUnreadMessages,
    });
    const initialCache = getCachedTickets(cacheKey);
    const [loading, setLoading] = useState(!initialCache);
    const [hasMore, setHasMore] = useState(initialCache?.hasMore || false);
    const [tickets, setTickets] = useState(initialCache?.tickets || []);
    const [count, setCount] = useState(initialCache?.count || 0);

    useEffect(() => {
        let isCancelled = false;
        const cached = getCachedTickets(cacheKey);

        if (cached) {
            setTickets(cached.tickets);
            setHasMore(cached.hasMore);
            setCount(cached.count);
            setLoading(false);
        } else {
            setTickets([]);
            setHasMore(false);
            setCount(0);
            setLoading(true);
        }

        const delayDebounceFn = setTimeout(() => {
            const fetchTickets = async() => {
                try {
                    const { data } = await api.get("/tickets", {
                        params: {
                            searchParam,
                            pageNumber,
                            status,
                            date,
                            showAll,
                            queueIds,
                            withUnreadMessages,
                        },
                    })
                    if (isCancelled) {
                        return;
                    }
                    ticketsCache.set(cacheKey, {
                        tickets: data.tickets,
                        hasMore: data.hasMore,
                        count: data.count,
                        updatedAt: Date.now(),
                    });
                    setTickets(data.tickets)

                    let horasFecharAutomaticamente = getHoursCloseTicketsAuto(); 

                    if (status === "open" && horasFecharAutomaticamente && horasFecharAutomaticamente !== "" &&
                        horasFecharAutomaticamente !== "0" && Number(horasFecharAutomaticamente) > 0) {

                        let dataLimite = new Date()
                        dataLimite.setHours(dataLimite.getHours() - Number(horasFecharAutomaticamente))

                        data.tickets.forEach(ticket => {
                            if (ticket.status !== "closed") {
                                let dataUltimaInteracaoChamado = new Date(ticket.updatedAt)
                                if (dataUltimaInteracaoChamado < dataLimite)
                                    closeTicket(ticket)
                            }
                        })
                    }

                    setHasMore(data.hasMore)
                    setCount(data.count)
                    setLoading(false)
                } catch (err) {
                    if (isCancelled) {
                        return;
                    }
                    setLoading(false)
                    toastError(err)
                }
            }

            const closeTicket = async(ticket) => {
                await api.put(`/tickets/${ticket.id}`, {
                    status: "closed",
                    userId: ticket.userId || null,
                })
            }

            fetchTickets()
        }, searchParam ? 500 : 0)
        return () => {
            isCancelled = true;
            clearTimeout(delayDebounceFn)
        }
    }, [
        searchParam,
        pageNumber,
        status,
        date,
        showAll,
        queueIds,
        withUnreadMessages,
        refreshKey,
        cacheKey,
    ])

    return { tickets, loading, hasMore, count };
};

export default useTickets;
